<template>
  <div class="landing-page">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-content">
        <h1 class="hero-title">GraphRM</h1>
        <p class="hero-subtitle">Streamline Your Cybersecurity Audits</p>
        <p class="hero-description">
          Stop wasting hours in Excel. Generate professional risk and stakeholder maps in minutes, not days.
        </p>
        <div class="cta-buttons">
          <button @click="navigateToLogin" class="btn-primary">Get Started</button>
          <button @click="scrollToFeatures" class="btn-secondary">Learn More</button>
        </div>
      </div>
    </section>

    <!-- Problem Section -->
    <section class="problem-section">
      <div class="container">
        <h2 class="section-title">The Problem</h2>
        <p class="section-text">
          Most cybersecurity freelancers use Excel to generate their audit documents. But this is time-consuming 
          and does not allow for easy diagram generation. Every hour spent on manual formatting is an hour lost 
          from billable consulting work.
        </p>
      </div>
    </section>

    <!-- Solution Section -->
    <section class="solution-section">
      <div class="container">
        <h2 class="section-title">The Solution</h2>
        <p class="section-text">
          To eliminate this loss of profitability, we developed <strong>GraphRM</strong>. 
          The idea is simple: you choose a diagram (for example, a Risk Map), follow the wizard, 
          and the tool generates your risk and stakeholder maps, ready to be integrated into your audit reports.
        </p>
        <div class="video-container">
          <video
            class="intro-video"
            :src="introVideo"
            controls
            playsinline
            preload="metadata"
          >
            Your browser does not support the video tag.
          </video>
        </div>
        <div class="preview-container">
          <img :src="riskMapPreview" alt="Risk Map Preview" class="risk-map-preview" />
        </div>
        <div class="features-grid">
          <div class="feature-card">
            <div class="feature-icon">🚀</div>
            <h3 class="feature-title">Save Time</h3>
            <p class="feature-text">Generate professional diagrams in minutes instead of hours</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">📊</div>
            <h3 class="feature-title">Professional Output</h3>
            <p class="feature-text">Export-ready risk and stakeholder maps that integrate seamlessly</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">🎯</div>
            <h3 class="feature-title">Easy to Use</h3>
            <p class="feature-text">Intuitive UI with a focus on your needs</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Beta Section -->
    <section class="beta-section" id="features">
      <div class="container">
        <h2 class="section-title">Join Our Closed Beta</h2>
        <p class="section-text">
          We are finalizing the closed beta and looking for <strong>five independent consultants</strong> 
          to test the tool in real-world conditions. In exchange for your feedback, we are offering you 
          the opportunity to use affiliate links to earn a share of the revenue from new users.
        </p>
        <div class="benefits-list">
          <div class="benefit-item">
            <span class="benefit-check">✓</span>
            <span>Early access to cutting-edge audit tools</span>
          </div>
          <div class="benefit-item">
            <span class="benefit-check">✓</span>
            <span>Shape the product with your feedback</span>
          </div>
          <div class="benefit-item">
            <span class="benefit-check">✓</span>
            <span>Earn revenue through affiliate program</span>
          </div>
          <div class="benefit-item">
            <span class="benefit-check">✓</span>
            <span>Save several hours on your next audit</span>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <h2 class="cta-title">Ready to Transform Your Audit Process?</h2>
        <p class="cta-text">Are you interested in saving several hours on your next audit?</p>
        <p class="cta-text">To join the closed beta, contact us at <a href="mailto:contact@graphrm.com" class="contact-link">contact@graphrm.com</a></p>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router';
import riskMapPreview from '@/assets/risk_map_example.png';
import introVideo from '@/assets/graphrm_introduction.mp4';

const router = useRouter();

const navigateToLogin = () => {
  router.push('/login');
};

const scrollToFeatures = () => {
  const element = document.getElementById('features');
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};
</script>

<style scoped>
.landing-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #0f1419 0%, #1a1f2e 100%);
  color: white;
  width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
}

/* Hero Section */
.hero-section {
  padding: 120px 20px 80px;
  text-align: center;
  background: linear-gradient(135deg, #1a1f2e 0%, #2d3748 100%);
  width: 100%;
  box-sizing: border-box;
}

.hero-content {
  max-width: 800px;
  margin: 0 auto;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #60a5fa 0%, #34d399 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
}

.hero-subtitle {
  font-size: 1.75rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #e2e8f0;
}

.hero-description {
  font-size: 1.25rem;
  color: #cbd5e1;
  margin-bottom: 2.5rem;
  line-height: 1.6;
}

.cta-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.btn-primary {
  padding: 1rem 2.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  background: linear-gradient(135deg, #3b82f6 0%, #10b981 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.4);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.6);
}

.btn-secondary {
  padding: 1rem 2.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  background: transparent;
  color: #60a5fa;
  border: 2px solid #60a5fa;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-secondary:hover {
  background: rgba(96, 165, 250, 0.1);
  transform: translateY(-2px);
}

.btn-large {
  padding: 1.25rem 3rem;
  font-size: 1.25rem;
}

/* Container */
.container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
  width: 100%;
  box-sizing: border-box;
}

/* Problem Section */
.problem-section {
  padding: 80px 20px;
  background: #131827;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  text-align: center;
  color: #f1f5f9;
}

.section-text {
  font-size: 1.125rem;
  line-height: 1.8;
  color: #cbd5e1;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
}

.section-text strong {
  color: #60a5fa;
  font-weight: 600;
}

/* Video Container */
.video-container {
  margin: 3rem auto;
  max-width: 1200px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.intro-video {
  width: 100%;
  max-width: 1200px;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  border: 1px solid #2d3748;
  background: #0f1419;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.intro-video:hover {
  box-shadow: 0 15px 50px rgba(96, 165, 250, 0.3);
}

/* Preview Container */
.preview-container {
  margin: 3rem auto;
  max-width: 1200px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.risk-map-preview {
  width: 100%;
  max-width: 1200px;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  border: 1px solid #2d3748;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.risk-map-preview:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 50px rgba(96, 165, 250, 0.3);
}

/* Solution Section */
.solution-section {
  padding: 80px 20px;
  background: #1a1f2e;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
}

.feature-card {
  background: #131827;
  padding: 2rem;
  border-radius: 12px;
  border: 1px solid #2d3748;
  text-align: center;
  transition: all 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-5px);
  border-color: #60a5fa;
  box-shadow: 0 10px 30px rgba(96, 165, 250, 0.2);
}

.feature-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.feature-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: #f1f5f9;
}

.feature-text {
  font-size: 1rem;
  color: #cbd5e1;
  line-height: 1.6;
}

/* Beta Section */
.beta-section {
  padding: 80px 20px;
  background: #131827;
}

.benefits-list {
  max-width: 600px;
  margin: 2rem auto 0;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #1a1f2e;
  border-radius: 8px;
  border-left: 4px solid #10b981;
  font-size: 1.125rem;
  color: #e2e8f0;
}

.benefit-check {
  font-size: 1.5rem;
  color: #10b981;
  font-weight: bold;
  flex-shrink: 0;
}

/* CTA Section */
.cta-section {
  padding: 100px 20px;
  text-align: center;
  background: linear-gradient(135deg, #1a1f2e 0%, #2d3748 100%);
}

.cta-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #f1f5f9;
}

.cta-text {
  font-size: 1.25rem;
  color: #cbd5e1;
  margin-bottom: 2.5rem;
}

.btn-contact {
  display: inline-block;
  text-decoration: none;
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-title {
    font-size: 2.5rem;
  }

  .hero-subtitle {
    font-size: 1.5rem;
  }

  .hero-description {
    font-size: 1.125rem;
  }

  .section-title {
    font-size: 2rem;
  }

  .cta-title {
    font-size: 2rem;
  }

  .features-grid {
    grid-template-columns: 1fr;
  }

  .video-container {
    margin: 2rem auto;
  }

  .intro-video {
    border-radius: 8px;
  }

  .preview-container {
    margin: 2rem auto;
  }

  .risk-map-preview {
    border-radius: 8px;
  }

  .cta-buttons {
    flex-direction: column;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
  }
}
</style>
